import React, { useContext, useState } from "react";
import moment from "moment";
import Slide from "@mui/material/Slide";
import Dialog from "@mui/material/Dialog";
import DialogContent from "@mui/material/DialogContent";
import styles from "./index.module.scss";
import Select from "../../FormControl/Select/Select";
import DatePickerWithInput from "Components/NewOrder/DatePicker/DatePickerWithInput";
import { getDeliveryTimes, transferOrderBurqToOnfleet } from "api/order";
import { AppContext } from "Context/AppContext";
import ConfirmationForTransferToOnfleet from "./ConfirmationForTransferToOnfleet";
import { useNavigate } from "react-router-dom";
import Datetime from "react-datetime";
import { CUSTOM_ANYTIME, pstToUTCDateTime, timeStamptoPSTime, utcDateTimeToTime } from "Pages/Order/Order/utility/util";
import { ORDER_DELIVERY, ORDER_DELIVERY_WITHOUT_ANYTIME } from "Pages/Order/Order/component/delivery";

const CUSTOM_ID = -1;

const Transition = React.forwardRef(function Transition(props, ref) {
  return <Slide direction="up" ref={ref} {...props} />;
});

const TransferToOnFleetModal = ({ showModal, pageTitle, handleCancelClick, close, orderDetails }) => {
  const { showToastMessage, lastOrderEditPath, setIsLoading } = useContext(AppContext);
  
  const [timeSelectionDropdown, setTimeSelectionDropdown] = useState(false);
  
  const [options, setOptions] = useState([]);
  
  const [selectTime, setSelectTime] = useState(null)
  
  const [isTimeSelect, setTimeSelected] = useState("none")
  
  const [isDateSelected, setDateSelected] = useState("none")
  
  const [selectedOnFleetOption, setSelectedOnFleetOption] = useState(null)
  
  const [open, setOpen] = useState(false)
  const navigate = useNavigate();

  const [deliveryDate, setDeliveryDate] = useState(null)

  // Custom delivery: "Delivered By" value and picker time
  const [customType, setCustomType] = useState("")
  const [customTime, setCustomTime] = useState(null)
  const [isCustomTypeSelect, setCustomTypeSelected] = useState("none")
  
  const handleDeliveryDateChange = async (day) => {
    try {
      if (day) { setTimeSelectionDropdown(true) }
      setOptions([]);
      
      let bodySend = {
        address_type_id: orderDetails.order_recipients[0].address_type_id,
        delivery_date: moment(day).format("MM/DD/YYYY"),
        full_address: orderDetails.order_recipients[0].recipient_address,
        order_by_time: moment().unix(),
        zip_code: orderDetails.order_recipients[0].zipcode,
        address_2: orderDetails.order_recipients[0].address_line_2 ?? "",
        attn: orderDetails.order_recipients[0].attn ?? "",
        orderTimezone: orderDetails.store.timezone
      }
      
      setDeliveryDate(bodySend.delivery_date)
      setTimeSelected("none")
      setDateSelected("none")
      
      const deliveryTimes = await getDeliveryTimes(bodySend)
      if (deliveryTimes && deliveryTimes.status === 200) {
        setTimeSelectionDropdown(false)
        setSelectTime(null)
        setOnFleetOptions(deliveryTimes.data.data, day, bodySend.delivery_date)
      } else {
        showToastMessage(undefined, "Delivery slot are currently not available", "error", false);
      }
    } catch (error) {
      showToastMessage(undefined, error.data.message, "error", false);
    }
  }

  const setOnFleetOptions = (data, day, delivery_date)=>{
    let options = [];
    data?.costList?.forEach((mat, idx) => {
      let delivery_time_type = mat.delivery_time_type;
      
      let pst_time = timeStamptoPSTime(mat.delivery_time, orderDetails.store.timezone);
      
      let delivery_date_time = pstToUTCDateTime(day, mat.delivery_time, null, orderDetails.store.timezone);
      
      let utc_time = utcDateTimeToTime(delivery_date_time);
      
      let name = `${delivery_time_type} ${pst_time} for $${mat.price}`;
      let delivery_time = `${delivery_time_type} ${utc_time}`;
      
      if (delivery_time_type === "Anytime") {
        name = `${delivery_time_type} for $${mat.price}`;
      }
      
      let onFleetOption = {
        id: idx+1,
        name: name,
        delivery_time_type: delivery_time_type,
        data: mat,
        delivery_date_time: delivery_date_time,
        delivery_date: moment(delivery_date, "MM/DD/YYYY").format("YYYY-MM-DD"),
        delivered_time: delivery_time,
        delivery_time_id: mat.id || "",
      }
      options.push(onFleetOption)
    })
    options.push({ id: CUSTOM_ID, name: "Custom" })
    setOptions((prev)=>[...prev, ...options])
    return true
  }

  const handleChangeToOnFleet = async (event) => {
    try {
      setIsLoading(true)
      
      let sendBody = {
        order_id: orderDetails.id,
        order_type: orderDetails.order_type || 0,
        delivery_type_id: orderDetails.delivery_type_id || "",
        is_local_order: orderDetails.is_local_order || 1,
        is_subscribe_order: orderDetails.is_subscribe_order || 0,
        is_burq_order: 0, // Mark order as no longer a Burq order
        delivery_date: selectedOnFleetOption.delivery_date,
        delivery_date_time: selectedOnFleetOption.delivery_date_time,
        delivered_time_id: selectedOnFleetOption.delivery_time_id ?? "",
        delivered_time: selectedOnFleetOption.delivered_time,
        delivery_time_type: selectedOnFleetOption.delivery_time_type || "",
      }

      const response = await transferOrderBurqToOnfleet(sendBody);
      if (response && response.status === 200) {
        showToastMessage(undefined, response.data.message || "Order type updated successfully", "success", false, 9000)
        close(false)
        setOpen(false)
        setIsLoading(false);
        navigate(lastOrderEditPath);
      } else {
        showToastMessage(undefined, response.data.message || "Something went wrong", "error", false);
        setIsLoading(false);
        setOpen(false)
        close(false)
    }
    } catch (error) {
      showToastMessage(undefined, error?.response?.data?.message || "Something went wrong", "error", false);
      setIsLoading(false);
      setOpen(false)
      close(false)
    }
  }

  const handleDeliveryMatrixOptionSelection = (selectedOptionId) => {
    setSelectedOnFleetOption(null)
    const selectedOption = options.find((option) => option.id === selectedOptionId);
    setSelectedOnFleetOption(selectedOption)
    setSelectTime(selectedOptionId)
    setTimeSelected("none")
    setDateSelected("none")
  }

  const buildCustomOption = () => {
    const delivery_date = moment(deliveryDate, "MM/DD/YYYY").format("YYYY-MM-DD");
    const pstTime = customType === "Anytime"
      ? moment(`${moment().format("YYYY-MM-DD")} ${CUSTOM_ANYTIME}`, "YYYY-MM-DD hh:mm A").format("hh:mm A")
      : moment(customTime).format("hh:mm A");
    const delivery_date_time = pstToUTCDateTime(delivery_date, null, pstTime, orderDetails.store.timezone);
    return {
      delivery_date,
      delivery_date_time,
      delivered_time: utcDateTimeToTime(delivery_date_time),
      delivery_time_type: customType,
      delivery_time_id: 0,
    };
  }

  const handleValidation = (event) => {
    const isCustom = selectTime === CUSTOM_ID;
    const typeError = isCustom && !customType;
    const hasCustomTime = customTime && moment(customTime).isValid();
    const timeError = selectTime === null || (isCustom && customType !== "Anytime" && !hasCustomTime);
    setDateSelected(deliveryDate === null ? "block" : "none");
    setTimeSelected(timeError ? "block" : "none");
    setCustomTypeSelected(typeError ? "block" : "none");
    if (deliveryDate === null || timeError || typeError) return;
    if (isCustom) setSelectedOnFleetOption(buildCustomOption());
    setOpen(true);
  }
  return (
    <React.Fragment>
      {selectTime !== null ? <ConfirmationForTransferToOnfleet
        open={open}
        setOpen={setOpen}
        changeToOnFleet={handleChangeToOnFleet}
      /> : ""}
      
      <Dialog
        open={showModal}
        TransitionComponent={Transition}
        onClose={handleCancelClick}
        classes={{
          paper: styles.modalDialog,
        }}
        aria-labelledby="modal-slide-title"
        aria-describedby="modal-slide-description"
        PaperProps={{
          style: {
            height: selectTime === CUSTOM_ID ? "auto" : undefined,
            minHeight: selectTime === CUSTOM_ID ? "380px" : "28vh",
            minWidth: "45vw",
            overflow: "visible",
          },
        }}>
        <DialogContent className={`${styles.modalContent} `}>
          <div className={`text-center ${styles.modalBody}`}>
            <h5 className={`text-center ${styles.modalTitle}`}>
              {pageTitle}
            </h5>
            
            <div className={`row px-1 py-1 text-center`}>
              <div className="col-md-6" style={{ textAlign: "left", position: "relative" }}>
                <DatePickerWithInput
                  label="Delivery Date"
                  blockDates={[]}
                  value={deliveryDate ? deliveryDate : null}
                  onChange={handleDeliveryDateChange}
                  datePickerclass={styles.datePicker}
                  showDateclass={true}
                  orderTimezone={orderDetails.store.timezone}
                />
                <span className="mt-3" style={{ color: "red", display: isDateSelected, position: "absolute" }}>Please select delivery date</span>
              </div>
              
              <div className="col-md-6 mt-2" style={{ textAlign: "left", position: "relative" }}>
                {options || timeSelectionDropdown ?
                  (
                    timeSelectionDropdown ?
                      <Select
                        label="Calculating..."
                        value={""}
                        onChange={""}
                        options={[]}
                      /> :
                      <Select
                        label="Delivery Time"
                        value={selectTime}
                        onChange={handleDeliveryMatrixOptionSelection}
                        options={options}
                      />
                  )
                  :
                  <Select
                    label="Delivery Time"
                    value={selectTime}
                    onChange={handleDeliveryMatrixOptionSelection}
                    options={[]} />}
                <span style={{ color: "red", display: isTimeSelect, position: "absolute" }}>Please select delivery time</span>
              </div>
            </div>

            {selectTime === CUSTOM_ID &&
              <div className={`row px-1 text-center`}>
                <div className="col-md-6" style={{ textAlign: "left", position: "relative" }}>
                  <Select
                    label="Delivered By"
                    value={customType}
                    onChange={(type) => { setCustomType(type); setCustomTypeSelected("none") }}
                    options={orderDetails.order_recipients[0].address_type_id > 2 ? ORDER_DELIVERY_WITHOUT_ANYTIME : ORDER_DELIVERY}
                  />
                  <span style={{ color: "red", display: isCustomTypeSelect, position: "absolute" }}>Please select delivered by</span>
                </div>
                {customType && customType !== "Anytime" &&
                  <div className="col-md-6" style={{ textAlign: "left", paddingTop: 16, marginBottom: 16 }}>
                    <Datetime
                      value={customTime}
                      dateFormat={false}
                      input={true}
                      inputProps={{ placeholder: "Select delivery time" }}
                      onChange={(value) => { setCustomTime(value); setTimeSelected("none") }}
                    />
                  </div>
                }
              </div>
            }

            <div style={selectTime === CUSTOM_ID ? { textAlign: "center", marginTop: 8 } : { position: "absolute", bottom: 20, right: "45%" }}>
              <button
                onClick={handleValidation}
                className={`btn gray-btn d-inline-block mb-3 mr-2`}
                style={{ color: "white" }}>
                Save
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </React.Fragment>
  );
};

export default TransferToOnFleetModal;
